import React, { useState, useEffect } from 'react';

interface DateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (fromDate: string, toDate: string) => void;
  minDate: string;
  maxDate: string;
  title: string;
}

const DateRangeModal: React.FC<DateRangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  minDate,
  maxDate,
  title
}) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [errors, setErrors] = useState<{ fromDate?: string; toDate?: string }>({});

  // Format date for display (e.g., "Aug 5, 2025")
  const formatDateForDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFromDate('');
      setToDate('');
      setErrors({});
    }
  }, [isOpen]);

  const validateDates = () => {
    const newErrors: { fromDate?: string; toDate?: string } = {};

    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      if (from > to) {
        newErrors.fromDate = 'Start date cannot be after end date';
        newErrors.toDate = 'End date cannot be before start date';
      }
    }

    if (fromDate) {
      const from = new Date(fromDate);
      const min = new Date(minDate);
      const max = new Date(maxDate);
      
      if (from < min || from > max) {
        newErrors.fromDate = `Date must be between ${minDate} and ${maxDate}`;
      }
    }

    if (toDate) {
      const to = new Date(toDate);
      const min = new Date(minDate);
      const max = new Date(maxDate);
      
      if (to < min || to > max) {
        newErrors.toDate = `Date must be between ${minDate} and ${maxDate}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateDates()) {
      onConfirm(fromDate, toDate);
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4" onKeyDown={handleKeyPress}>
        <h2 className="text-xl font-bold mb-4 text-gray-800">{title}</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              min={minDate}
              max={maxDate}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.fromDate ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Select start date"
            />
            {errors.fromDate && (
              <p className="text-red-500 text-xs mt-1">{errors.fromDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={minDate}
              max={maxDate}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.toDate ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Select end date"
            />
            {errors.toDate && (
              <p className="text-red-500 text-xs mt-1">{errors.toDate}</p>
            )}
          </div>

          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Available date range:</strong> {formatDateForDisplay(minDate)} to {formatDateForDisplay(maxDate)}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Leave both fields empty to include all entries
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Generate PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangeModal;
